import { useEffect, useMemo } from 'react'
import { EditUsageDrawerProps } from './types'
import { FormProvider, useForm } from 'react-hook-form'
import { UsageStepFormInputs } from 'features/simulation/steps/types'
import { determineDefaultUsageFormValues } from 'features/usage-form/utils'
import UsageForm from 'features/usage-form/UsageForm'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { updateStoreForUsageStep } from 'features/simulation/steps/utils'
import { useTranslation } from 'next-i18next/pages'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { CustomerFlows, InputGroupKeys } from 'store/customer/enums'
import { REGISTRATION_FLOW } from 'features/registration/constants'
import { RegistrationSteps } from 'features/registration/types'
import { goToStep } from 'store/customer/slice'
import { Drawer } from '@boltenergy-be/design-system'
import { COUNTER_TYPES, METER_TYPES, SIMULATION_TYPES } from '@boltenergy-be/shared'

const EditUsageDrawer = ({ isOpen, onRequestClose }: EditUsageDrawerProps) => {
  // Redux
  const { inputs, flows } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('simulation')

  // React Hook Form
  const defaultValues = useMemo<UsageStepFormInputs>(() => determineDefaultUsageFormValues(inputs), [inputs])
  const hookForm = useForm<UsageStepFormInputs>({
    defaultValues,
    mode: 'onBlur'
  })
  const { getValues, reset, watch } = hookForm

  // Update the values when the defaultValues are changed
  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  // Constants
  const isAfterMeterDetailsStep =
    REGISTRATION_FLOW.indexOf(flows[CustomerFlows.REGISTRATION].currentStep) > REGISTRATION_FLOW.indexOf(RegistrationSteps.METER_DETAILS)
  const isSwitchingEnergyType =
    !inputs[InputGroupKeys.DELIVERY_ADDRESS].needsGas && watch('needsGas') !== inputs[InputGroupKeys.DELIVERY_ADDRESS].needsGas
  const isSwitchingMeterType =
    inputs[InputGroupKeys.SIMULATION].meterType === METER_TYPES.double && watch('meterType') !== inputs[InputGroupKeys.SIMULATION].meterType
  const isSwitchingCounterType =
    inputs[InputGroupKeys.METER_DETAILS].counterType === COUNTER_TYPES.analog &&
    watch('counterType') !== inputs[InputGroupKeys.METER_DETAILS].counterType
  const isSwitchingExclNight =
    !inputs[InputGroupKeys.SIMULATION].hasExclusiveNightMeter &&
    watch('hasExclNight') !== inputs[InputGroupKeys.SIMULATION].hasExclusiveNightMeter
  const isSwitchingMeterDetailsAlteringInfo =
    isSwitchingEnergyType || isSwitchingMeterType || isSwitchingCounterType || isSwitchingExclNight

  /**
   * Handles the form submit
   */
  const onSubmit = async () => {
    const data = getValues()

    updateStoreForUsageStep(data)
    await requestAndSavePropositions()

    // Return to meter details step because some important information might be missing
    if (isAfterMeterDetailsStep) {
      dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.METER_DETAILS }))
    }

    onRequestClose()
  }

  return (
    <Drawer
      onRequestClose={() => {
        onRequestClose()
        reset()
      }}
      {...{ isOpen }}
      title={t('steps.usage.estimatedUsage.title')}
      titleVariant="h3"
    >
      <FormProvider {...hookForm}>
        <UsageForm
          hasEnergyTypeToggle={inputs.simulation.chosenSimulationType !== SIMULATION_TYPES.express}
          warning={isAfterMeterDetailsStep && isSwitchingMeterDetailsAlteringInfo ? t('usage.estimatedUsage.warning') : undefined}
          {...{ onSubmit }}
        />
      </FormProvider>
    </Drawer>
  )
}

export default EditUsageDrawer
